/* @flow */

import {
  FloorHeight,
  GridWidth,
  GridHeight,
  UmbrellaLength,
  DefaultParticleHoseHeight,
} from './Common';
import type { UmbrellaConfig } from './Level';
import Level from './Level';

const LIGHT_BLUE = 'lightblue';
const LIGHT_GREEN = 'lightgreen';
const YELLOW = 'yellow';
const SALMON = '#FA8072';
const PLUM = '#DDA0DD';
const CORN_FLOWER = '#6495ED';

function generateUmbrellas(numUmbrellas: number): Array<UmbrellaConfig> {
  return [...Array(numUmbrellas).keys()]
    .map((trash, index) => ({
      bottomCenter: { x: UmbrellaLength / 3, y: UmbrellaLength + (index * UmbrellaLength) / 3 },
      radians: 0.7,
      rampHypotenuse: UmbrellaLength,
    }))
    .reverse();
}

const defaultLevel = {
  test: false,
  topMessage: [],
  floatingRamps: [],
  platforms: [],
};
const defaultLevelTutorial = {
  ...defaultLevel,
  songKey: 'amalgamize',
};
const defaultLevelWorldA = {
  ...defaultLevel,
  songKey: 'amalgamize',
};
const defaultLevelTest = {
  ...defaultLevel,
  test: true,
  songKey: 'amalgamize',
  autoplayTracks: [],
};

const levelConfigs = [
  {
    ...defaultLevelTutorial,
    key: '0a_one_goal',
    umbrellas: generateUmbrellas(1),
    topMessage: ['grab the umbrella with [S] and use it to redirect the water'],
    autoplayTracks: [],
    goals: [
      {
        position: { x: 1500, y: FloorHeight },
        acceptedColors: [LIGHT_BLUE],
        tracks: ['Synth1'],
      },
    ],
    particleHoses: [
      {
        origin: { x: 2250, y: DefaultParticleHoseHeight },
        color: LIGHT_BLUE,
      },
    ],
  },
  {
    ...defaultLevelTutorial,
    key: '0b_two_goal',
    umbrellas: generateUmbrellas(1),
    topMessage: ['grab the umbrella, then use [up/down arrow] to adjust the spray'],
    autoplayTracks: ['Synth1'],
    goals: [
      {
        position: { x: 750, y: FloorHeight },
        acceptedColors: [YELLOW],
        tracks: ['HiHat'],
      },
      {
        position: { x: 3250, y: FloorHeight },
        acceptedColors: [YELLOW],
        tracks: ['Organ'],
      },
    ],
    particleHoses: [
      {
        origin: { x: 2000, y: DefaultParticleHoseHeight },
        color: YELLOW,
      },
    ],
  },
  {
    ...defaultLevelTutorial,
    key: '0c_wrong_color',
    umbrellas: generateUmbrellas(2),
    topMessage: [
      'the green flower only wants green water! stop the red water from getting in the way',
    ],
    autoplayTracks: ['Synth1', 'HiHat', 'Organ'],
    goals: [
      {
        position: { x: 1800, y: FloorHeight },
        acceptedColors: [LIGHT_GREEN],
        tracks: ['Synth3'],
      },
    ],
    particleHoses: [
      {
        origin: { x: 1800, y: DefaultParticleHoseHeight },
        color: SALMON,
      },
      {
        origin: { x: 2700, y: DefaultParticleHoseHeight },
        color: LIGHT_GREEN,
      },
    ],
  },
  {
    ...defaultLevelTutorial,
    key: '1a_blocking_ramp',
    umbrellas: generateUmbrellas(3),
    // topMessage: ['use [A] to jump on top things so you can place your umbrella even higher'],
    autoplayTracks: [],
    goals: [
      {
        position: { x: GridWidth - 250, y: FloorHeight },
        acceptedColors: [CORN_FLOWER],
        tracks: ['Bass1'],
      },
    ],
    particleHoses: [
      {
        origin: { x: 1000, y: DefaultParticleHoseHeight },
        color: CORN_FLOWER,
      },
    ],
    floatingRamps: [
      {
        bottomCenter: { x: GridWidth - 500, y: GridHeight / 3 },
        hypotenuse: 1500,
        radians: 0.5,
      },
    ],
  },
  {
    ...defaultLevelTutorial,
    key: '1b_two_pipe_three_goal',
    umbrellas: generateUmbrellas(4),
    // topMessage: [
    //   'try staggering the umbrellas under a stream to send one stream multiple distances',
    // ],
    autoplayTracks: ['Bass1'],
    goals: [
      {
        position: { x: 1200, y: FloorHeight },
        acceptedColors: [PLUM],
        tracks: ['HiHat'],
      },
      {
        position: { x: 2100, y: FloorHeight },
        acceptedColors: [PLUM],
        tracks: ['Bass2'],
      },
      {
        position: { x: 3000, y: FloorHeight },
        acceptedColors: [PLUM],
        tracks: ['Synth1'],
      },
    ],
    particleHoses: [
      {
        origin: { x: 600, y: DefaultParticleHoseHeight },
        color: PLUM,
      },
      {
        origin: { x: 3600, y: DefaultParticleHoseHeight },
        color: PLUM,
      },
    ],
  },
  {
    ...defaultLevelWorldA,
    key: '1c_two_goal_cross',
    umbrellas: generateUmbrellas(2),
    autoplayTracks: ['Bass1', 'Bass2', 'HiHat', 'Synth1'],
    goals: [
      {
        position: { x: 1500, y: FloorHeight },
        acceptedColors: [YELLOW],
        tracks: ['Organ'],
      },
      {
        position: { x: 3300, y: FloorHeight },
        acceptedColors: [PLUM],
        tracks: ['Synth3'],
      },
    ],
    particleHoses: [
      {
        origin: { x: 1950, y: DefaultParticleHoseHeight },
        color: PLUM,
      },
      {
        origin: { x: 2850, y: DefaultParticleHoseHeight },
        color: YELLOW,
      },
    ],
  },
  {
    ...defaultLevelWorldA,
    key: '2a_one_pipe_three_goal',
    umbrellas: generateUmbrellas(3),
    autoplayTracks: [],
    goals: [
      {
        position: { x: 1200, y: FloorHeight },
        acceptedColors: [LIGHT_BLUE],
        tracks: ['Organ'],
      },
      {
        position: { x: 2100, y: FloorHeight },
        acceptedColors: [LIGHT_BLUE],
        tracks: ['Synth2'],
      },
      {
        position: { x: 3000, y: FloorHeight },
        acceptedColors: [LIGHT_BLUE],
        tracks: ['HiHat'],
      },
    ],
    particleHoses: [
      {
        origin: { x: 2100, y: DefaultParticleHoseHeight },
        color: LIGHT_BLUE,
      },
    ],
  },
  {
    ...defaultLevelWorldA,
    key: '2b_three_goal_cross',
    umbrellas: generateUmbrellas(5),
    autoplayTracks: ['Organ', 'Synth2', 'HiHat'],
    goals: [
      {
        position: { x: 450, y: FloorHeight },
        acceptedColors: [SALMON],
        tracks: ['SynthLead1'],
      },
      {
        position: { x: 900, y: FloorHeight },
        acceptedColors: [LIGHT_GREEN],
        tracks: ['Synth1'],
      },
      {
        position: { x: 1350, y: FloorHeight },
        acceptedColors: [LIGHT_BLUE],
        tracks: ['Synth3'],
      },
      {
        position: { x: GridWidth - 300, y: FloorHeight },
        acceptedColors: [SALMON],
        tracks: ['SynthLead2'],
      },
    ],
    particleHoses: [
      {
        origin: { x: GridWidth - 2100, y: DefaultParticleHoseHeight },
        color: LIGHT_BLUE,
      },
      {
        origin: { x: GridWidth - 1200, y: DefaultParticleHoseHeight },
        color: LIGHT_GREEN,
      },
      {
        origin: { x: GridWidth - 300, y: DefaultParticleHoseHeight },
        color: SALMON,
      },
    ],
  },
  {
    ...defaultLevelTest,
    key: 't_platforms',
    umbrellas: generateUmbrellas(10),
    autoplayTracks: [],
    goals: [
      {
        position: { x: 450, y: FloorHeight },
        acceptedColors: [SALMON],
        tracks: ['Synth2'],
      },
    ],
    platforms: [
      {
        centerPosition: { x: 300, y: 300 },
        radians: -0.5,
        hypotenuse: 400,
      },
      {
        centerPosition: { x: 900, y: 900 },
        radians: 0,
        hypotenuse: 400,
      },
      {
        centerPosition: { x: 1500, y: 1500 },
        radians: 0.5,
        hypotenuse: 400,
      },
      {
        centerPosition: { x: 2100, y: 1500 },
        radians: 1,
        hypotenuse: 400,
      },
    ],
    particleHoses: [
      {
        origin: { x: GridWidth - 300, y: DefaultParticleHoseHeight },
        color: SALMON,
      },
    ],
  },
];

type LevelSummary = {
  key: string,
  name: string,
};
type WorldConfig = {
  name: string,
  levels: Array<LevelSummary>,
};

const mapping: Array<WorldConfig> = [
  {
    name: 'Tutorial',
    levels: [
      {
        key: '0a_one_goal',
        name: 'Level 1',
      },
      {
        key: '0b_two_goal',
        name: 'Level 2',
      },
      {
        key: '0c_wrong_color',
        name: 'Level 3',
      },
    ],
  },
  {
    name: 'World A',
    levels: [
      {
        key: '1a_blocking_ramp',
        name: 'Level 1',
      },
      {
        key: '1b_two_pipe_three_goal',
        name: 'Level 2',
      },
      {
        key: '1c_two_goal_cross',
        name: 'Level 3',
      },
    ],
  },
  {
    name: 'World B',
    levels: [
      {
        key: '2a_one_pipe_three_goal',
        name: 'Level 1',
      },
      {
        key: '2b_three_goal_cross',
        name: 'Level 2',
      },
    ],
  },
  {
    name: 'Test Levels',
    levels: [
      {
        key: 't_platforms',
        name: 'platforms',
      },
    ],
  },
];

class _WorldInfo {
  isTest = window.location.search.includes('test');

  getLevelsAsObject() {
    const { isTest } = this;
    return levelConfigs.reduce((map, lvl) => {
      if (isTest || !lvl.test) {
        map[lvl.key] = lvl;
      }
      return map;
    }, {});
  }
  getWorldAsObject() {
    const levelsObj = this.getLevelsAsObject();
    const prunedMap: Array<WorldConfig> = mapping.reduce((arr, w) => {
      const prunedWorld = {
        ...w,
        levels: w.levels.filter(lvl => levelsObj[lvl.key]),
      };
      if (prunedWorld.levels.length > 0) {
        arr.push(prunedWorld);
      }
      return arr;
    }, []);
    return prunedMap;
  }
  getLevelsAsArray() {
    const worldObj = this.getWorldAsObject();
    const levelsObj = this.getLevelsAsObject();
    const levelKeys: Array<string> = worldObj.reduce((arr, world) => {
      return arr.concat(world.levels.map(lvl => lvl.key), 'n/a');
    }, []);
    // $FlowFixMe
    return levelKeys.map(key => levelsObj[key]);
  }

  lookupLevelIndexForKey(levelKey: string) {
    const levelArr = this.getLevelsAsArray();
    return levelArr.findIndex(lvl => lvl && lvl.key === levelKey);
  }
  lookupLevelIndexForWorld(worldIndex: number) {
    const world = this.getWorldAsObject()[worldIndex];
    if (world) {
      const levelKey = this.getWorldAsObject()[worldIndex].levels[0].key;
      return this.lookupLevelIndexForKey(levelKey);
    }
    return -1;
  }

  loadLevel(onComplete: () => void, levelKey: string) {
    const levelsObj = this.getLevelsAsObject();
    const levelConfig = levelsObj[levelKey];
    return levelConfig && new Level(onComplete, levelConfig);
  }
  loadLevelByIndex(onComplete: () => void, levelIndex: number) {
    const levelsArr = this.getLevelsAsArray();
    const levelToLoad = levelsArr[levelIndex];
    return levelToLoad && this.loadLevel(onComplete, levelToLoad.key);
  }
}

const WorldInfo = new _WorldInfo();
export default WorldInfo;



// WEBPACK FOOTER //
// ./src/WorldInfo.js